// react
import { useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../Project.context';
import { ContextUser } from '../../../../../App.context';

// mui
import {
	Alert,
	Box,
	Button,
	Chip,
	Container,
	Divider,
	Grid,
	Paper,
	Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// NOTE: This settings section is temporary. V2 onboarding may ultimately
// happen exclusively through the project onboarding flow. This provides
// a way for WFA users to upgrade existing projects to v2 in the interim.

const FeatureRow = ({ label }: { label: string }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
		<CheckCircleOutlineIcon fontSize="small" color="primary" />
		<Typography variant="body2">{label}</Typography>
	</Box>
);

export const V2Onboarding = () => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project, refetchProject } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const isWfa = user?.role === 'WFA';
	const isV2 = project?.version === '2';

	const handleToggleV2 = async () => {
		if (!project || !isWfa) return;

		// Temporarily block reverting from V2 to Classic
		if (project.version === '2') {
			setError('Switching back to Fundschain 1.0 is not currently supported.');
			return;
		}

		setIsLoading(true);
		setError(null);
		setSuccess(null);

		try {
			const newValue = project.version !== '2';

			const res = await fetch(`${process.env.REACT_APP_API_URL}/projects/${project.id}`, {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
					User: String(idToken),
				},
				body: JSON.stringify({ version: newValue ? '2' : '1' }),
			});

			const data = await res.json();

			if (data.error) {
				throw new Error(data.error.message);
			}

			setSuccess(newValue ? t('alert.v2EnabledSuccess') : t('alert.v2DisabledSuccess'));

			refetchProject();
		} catch (err) {
			const message = err instanceof Error ? err.message : t('error.default');
			setError(message);
		} finally {
			setIsLoading(false);
		}
	};

	if (!project || !isWfa) {
		return null;
	}

	return (
		<Container maxWidth={false}>
			<Grid container spacing={3}>
				{/* Status Banner */}
				<Grid item xs={12}>
					<Paper
						sx={{
							p: 3,
							borderLeft: 4,
							borderColor: isV2 ? 'primary.main' : 'grey.400',
						}}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
									<Typography variant="h3">{t('v2Onboarding.heading')}</Typography>
									<Chip
										label={isV2 ? t('v2Onboarding.active') : t('v2Onboarding.classic')}
										color={isV2 ? 'primary' : 'default'}
										size="small"
										sx={{ fontWeight: 700 }}
									/>
								</Box>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
									{t('v2Onboarding.subtitle')}
								</Typography>
							</Grid>
						</Grid>
					</Paper>
				</Grid>

				{/* Current Experience Description */}
				<Grid item xs={12}>
					<Paper sx={{ p: 3 }}>
						<Typography variant="h4" gutterBottom>
							{isV2
								? t('v2Onboarding.currentExperience.v2Title')
								: t('v2Onboarding.currentExperience.v1Title')}
						</Typography>
						<Typography color="text.secondary" sx={{ mb: 2 }}>
							{isV2
								? t('v2Onboarding.currentExperience.v2Description')
								: t('v2Onboarding.currentExperience.v1Description')}
						</Typography>

						<Divider sx={{ my: 2 }} />

						<Grid container spacing={4}>
							{/* Classic Experience */}
							<Grid item xs={12} md={6}>
								<Paper
									variant="outlined"
									sx={{
										p: 2.5,
										height: '100%',
										borderColor: !isV2 ? 'primary.main' : 'grey.300',
										borderWidth: !isV2 ? 2 : 1,
										opacity: isV2 ? 0.7 : 1,
									}}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
										<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
											{t('v2Onboarding.features.classicTitle')}
										</Typography>
										{!isV2 && (
											<Chip
												label={t('v2Onboarding.features.currentLabel')}
												color="primary"
												size="small"
												sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
											/>
										)}
									</Box>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
										{t('v2Onboarding.features.classicDescription')}
									</Typography>
									<FeatureRow label={t('v2Onboarding.features.classicLedger')} />
									<FeatureRow label={t('v2Onboarding.features.classicInvoices')} />
									<FeatureRow label={t('v2Onboarding.features.classicStatements')} />
									<FeatureRow label={t('v2Onboarding.features.classicReporting')} />
								</Paper>
							</Grid>

							{/* Fundschain 2.0 Experience */}
							<Grid item xs={12} md={6}>
								<Paper
									variant="outlined"
									sx={{
										p: 2.5,
										height: '100%',
										borderColor: isV2 ? 'primary.main' : 'grey.300',
										borderWidth: isV2 ? 2 : 1,
										opacity: !isV2 ? 0.7 : 1,
									}}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
										<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
											{t('v2Onboarding.features.v2Title')}
										</Typography>
										{isV2 && (
											<Chip
												label={t('v2Onboarding.features.currentLabel')}
												color="primary"
												size="small"
												sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
											/>
										)}
									</Box>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
										{t('v2Onboarding.features.v2Description')}
									</Typography>
									<FeatureRow label={t('v2Onboarding.features.v2SpendPlans')} />
									<FeatureRow label={t('v2Onboarding.features.v2Blockchain')} />
									<FeatureRow label={t('v2Onboarding.features.v2Vouchers')} />
									<FeatureRow label={t('v2Onboarding.features.v2Envelopes')} />
								</Paper>
							</Grid>
						</Grid>

						<Alert severity="warning" sx={{ mt: 3 }}>
							{t('v2Onboarding.exclusiveNote')}
						</Alert>
					</Paper>
				</Grid>

				{/* Action Section */}
				<Grid item xs={12}>
					<Paper sx={{ p: 3 }}>
						<Typography variant="h4" gutterBottom>
							{isV2 ? t('v2Onboarding.action.revertTitle') : t('v2Onboarding.action.upgradeTitle')}
						</Typography>
						<Typography color="text.secondary" sx={{ mb: 2 }}>
							{isV2
								? t('v2Onboarding.action.revertDescription')
								: t('v2Onboarding.action.upgradeDescription')}
						</Typography>

						<Alert severity="info" sx={{ mb: 3 }}>
							{t('v2Onboarding.action.note')}
						</Alert>

						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						{success && (
							<Alert severity="success" sx={{ mb: 2 }}>
								{success}
							</Alert>
						)}

						<Button
							disabled={isLoading || isV2}
							onClick={handleToggleV2}
							variant="contained"
							color={isV2 ? 'error' : 'primary'}
							size="large">
							{isLoading
								? t('loading')
								: isV2
									? t('v2Onboarding.action.revertButton')
									: t('v2Onboarding.action.upgradeButton')}
						</Button>
					</Paper>
				</Grid>
			</Grid>
		</Container>
	);
};
